import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Dialog,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogContent,
} from "../../misc/Dialog";
import {
    JobStatus,
    MusicType,
    SelectedImages,
    SlideshowProps,
    SlideshowText,
} from "./types";
import { Button } from "../../misc/Button";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import ConfirmPopup from "../../misc/ConfirmPopup";
import {
    SERVICE_UNAVAILABLE_REASON,
    VIDEO_SERVICE_UNAVAILABLE_MESSAGE,
} from "../../../constants/video-service";
import axios from "axios";
import { getAuthHeader } from "../../../utils/authHeader";
import { FaTimes } from "react-icons/fa";
import { Loader2 } from "lucide-react";
import { MAX_IMAGES, MIN_IMAGES } from "./utils";
import SlideshowMediaSelector from "./SlideshowMediaSelector";
import SlideshowBranding from "./SlideshowBranding";
import SlideshowTextEditor from "./SlideshowTextEditor";
import SlideshowPreview from "./preview/SlideshowPreview";
import ClassicPreview from "./preview/ClassicPreview";
import { orderImagesByTag } from "./reorderByTag";
import { Switch } from "../../misc/Switch";
import {
    DEFAULT_SLIDESHOW_TEMPLATE,
    KEN_BURNS_MAX_IMAGES,
    SLIDESHOW_TEMPLATES,
    SlideshowTemplateId,
    isKenBurnsTemplate,
    isVideo1Template,
} from "./slideshowTemplates";
import {
    BrandColors,
    ColorSource,
} from "../../misc/custom-posts/CaptionedVideoMediaStep";
import { useSocket } from "../../../context/SocketContext";
import useAppSelector from "../../../hooks/useAppSelector";
import {
    AutoVideoLogoRef,
    BrandingSettingsResponse,
} from "../../../types/slideshow";
import { colorRefToSource } from "../../../utils/autoVideoColorRef";

const Slideshow: React.FC<SlideshowProps> = ({
    listingId,
    listingType,
    images,
    isOpen,
    onClose,
    isGenerating,
    setIsGenerating,
    assets,
    jobId,
    setJobId,
    setJobStatus,
    fetchListing,
    formattedAddress,
    recentlyListed,
}) => {
    const { socket, isAuthenticated } = useSocket();
    const corporateHeadshot = useAppSelector(
        (state) => state.auth.user?.corporateHeadshot
    );
    // Agent identity for the live preview's intro/outro cards. The real render
    // resolves these server-side from the branding profile; the preview sources
    // them from the auth user so the cards match.
    const agentFirstName = useAppSelector((state) => state.auth.user?.nameFirst);
    const agentLastName = useAppSelector((state) => state.auth.user?.nameLast);
    const agentPhone = useAppSelector((state) => state.auth.user?.phoneNumber);

    const [step, setStep] = useState(1);
    const [selectedImages, setSelectedImages] = useState<SelectedImages[]>([]);
    // The motion-template heads-up fires once per wizard run: going past the
    // ceiling, undoing it and going past again must not nag.
    const kenBurnsNoticeShown = useRef(false);
    const [showKenBurnsNotice, setShowKenBurnsNotice] = useState(false);
    // Smart Order: auto-arrange photos into a natural listing tour by tag. On by
    // default; the user can turn it off to keep their own selection order.
    const [smartOrder, setSmartOrder] = useState(true);
    const [selectedTemplate, setSelectedTemplate] =
        useState<SlideshowTemplateId>(DEFAULT_SLIDESHOW_TEMPLATE);
    const [colorSource, setColorSource] = useState<ColorSource | undefined>(
        undefined
    );
    const [brandColors, setBrandColors] = useState<BrandColors | undefined>(
        undefined
    );
    const [selectedFont, setSelectedFont] = useState<string>("Inter");
    const [texts, setTexts] = useState<SlideshowText[]>([]);
    // The original AI-generated text, captured on each (re)generate and sent
    // alongside the (possibly edited) `texts` so the backend can keep it for audit.
    const [aiTexts, setAiTexts] = useState<SlideshowText[]>([]);
    // MB-1314 (O3): the debug artifact recorded when these captions were generated, in
    // an earlier request. Sent back on create so it can be linked to the render.
    const [captionRenderId, setCaptionRenderId] = useState<string | undefined>();
    // True while the slide-text generate-text API is in flight (owned here so the
    // step-3 "Generate Slideshow" button stays disabled until it settles).
    const [isGeneratingText, setIsGeneratingText] = useState(false);
    const [selectedLogo, setSelectedLogo] = useState<string | null>(null);
    // Classic (Video 1) selected reusable address overlay (asset id) + its
    // resolved URL, used to composite the overlay in the Classic live preview.
    const [selectedOverlay, setSelectedOverlay] = useState<string | null>(null);
    const [selectedOverlayUrl, setSelectedOverlayUrl] = useState<string | null>(
        null
    );
    const [includeMusic, setIncludeMusic] = useState(false);
    // MB-1366: the saved Listing Video Settings logo ref — restored by the
    // logo picker exactly as the settings page does.
    const [savedLogoRef, setSavedLogoRef] = useState<AutoVideoLogoRef | null>(
        null
    );
    // Identifies the current open, so a superseded seed response can be dropped.
    const openSeqRef = useRef(0);
    // Live mirror of selectedMusic, so seeding can test it without capturing a
    // stale value in its async closure.
    const selectedMusicRef = useRef("");
    const [music, setMusic] = useState<MusicType[]>([]);
    const [selectedMusic, setSelectedMusic] = useState<string>("");
    // Set once the user tries to leave the branding step; gates inline errors.
    const [brandingValidateAttempted, setBrandingValidateAttempted] =
        useState(false);

    const subscribedSlideshowRef = useRef<Set<string>>(new Set());
    // Jobs whose outcome has already been shown, so the socket event and the
    // reconcile fetch can race without double-toasting (MB-1396).
    const settledJobsRef = useRef<Set<string>>(new Set());
    // The dialog body (not the header) is the scroll container and persists
    // across steps, so its scroll position must be reset to the top on each
    // step change.
    const bodyRef = useRef<HTMLDivElement>(null);

    // The address-overlay create form takes over step 2 (in place of the normal
    // branding controls) rather than opening a second popup.
    const [isCreatingOverlay, setIsCreatingOverlay] = useState(false);

    // MB-1366: seed the dialog from the user's saved Listing Video Settings
    // (Settings > Listing Video Settings) so manual slideshows inherit the
    // configured template, colors, font, logo and music instead of forcing
    // duplicate configuration. Called once per open, immediately after
    // resetState(); every field stays fully editable afterwards.
    // `openId` identifies the open that requested this seed. A response from a
    // superseded open — slow token refresh, or a quick close/reopen — is
    // dropped rather than overwriting what the user has since chosen.
    // Within the current open, each write only fills a field still sitting at
    // its resetState() default, so a slow response can't clobber a choice the
    // user made while it was in flight. That test has to read live state —
    // via a functional updater, or a ref for the music pair — because this
    // closure captured the opening values.
    const seedFromSettings = async (openId: number): Promise<void> => {
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const { data } = await axios.get<BrandingSettingsResponse>(
                `${backendUrl}/settings/branding`,
                { headers }
            );
            if (openId !== openSeqRef.current) return;
            const savedTemplate = data.autoVideoPreferredTemplates?.[0];
            if (
                savedTemplate &&
                SLIDESHOW_TEMPLATES.some((t) => t.id === savedTemplate)
            ) {
                setSelectedTemplate((cur) =>
                    cur === DEFAULT_SLIDESHOW_TEMPLATE
                        ? (savedTemplate as SlideshowTemplateId)
                        : cur
                );
            }
            // Mirror the settings page's brokerage fallback, otherwise a user
            // whose settings show brokerage colors gets no colors seeded here
            // at all. No custom/DEFAULT_BRAND_COLORS arm: leaving colorSource
            // undefined is this dialog's own "nothing chosen" state.
            const source =
                colorRefToSource(data.autoVideoColor) ??
                (data.brokerageId
                    ? { type: "brokerage" as const, id: data.brokerageId }
                    : undefined);
            if (source) setColorSource((cur) => cur ?? source);
            const savedFont = data.autoVideoFont;
            if (savedFont) {
                setSelectedFont((cur) => (cur === "Inter" ? savedFont : cur));
            }
            if (data.autoVideoLogo) {
                setSavedLogoRef((cur) => cur ?? data.autoVideoLogo ?? null);
            }
            const savedTrack = data.autoVideoMusic?.[0];
            // includeMusic and selectedMusic must be written together, so this
            // pair reads the live value from a ref rather than an updater.
            if (savedTrack && selectedMusicRef.current === "") {
                setIncludeMusic(true);
                setSelectedMusic(savedTrack);
            }
        } catch (error) {
            // Seeding is best-effort — the dialog works exactly as before
            // without it.
            console.error(
                "Failed to load Listing Video Settings defaults:",
                error
            );
        }
    };

    useEffect(() => {
        selectedMusicRef.current = selectedMusic;
    }, [selectedMusic]);

    useEffect(() => {
        bodyRef.current?.scrollTo({ top: 0 });
        setIsCreatingOverlay(false);
    }, [step]);

    // Classic (Video 1) uses the image-to-video flow: no text step, and logo +
    // headshot are optional (logo is rendered as an overlay only if provided).
    const isVideo1 = isVideo1Template(selectedTemplate);

    const exceedsKenBurnsLimit =
        isKenBurnsTemplate(selectedTemplate) &&
        selectedImages.length > KEN_BURNS_MAX_IMAGES;

    // A motion template chosen before the photo count went over its ceiling
    // (saved preference, or Back to add photos) can no longer be selected on
    // step 2, so fall back to the default and say why.
    const continueToBranding = (): void => {
        if (exceedsKenBurnsLimit) {
            setSelectedTemplate(DEFAULT_SLIDESHOW_TEMPLATE);
            setShowKenBurnsNotice(true);
        }
        setStep(2);
    };

    // Required branding fields with no default / that the user can leave empty.
    const brandingErrors = {
        logo: !isVideo1 && !selectedLogo,
        headshot: !isVideo1 && !corporateHeadshot,
        music: includeMusic && !selectedMusic,
    };
    const hasBrandingErrors =
        brandingErrors.logo || brandingErrors.headshot || brandingErrors.music;

    const continueFromBranding = (): void => {
        setBrandingValidateAttempted(true);
        if (hasBrandingErrors) {
            const missing = [
                brandingErrors.logo && "a logo",
                brandingErrors.headshot && "a corporate headshot",
                brandingErrors.music && "a music track",
            ].filter(Boolean);
            toast.error(
                <CustomToast
                    message={`Please add ${missing.join(", ")} to continue.`}
                    type="error"
                />,
                { autoClose: 4000 }
            );
            return;
        }
        setStep(3);
    };

    // Reorder the selected images into a natural listing-tour sequence by tag
    // (exterior → living → kitchen → beds → baths → outdoor). This ordering
    // drives the slide page, the AI slide text, and the order sent to the
    // renderer, so all three stay consistent.
    const orderedSelectedImages = useMemo(
        () =>
            smartOrder
                ? orderImagesByTag(selectedImages, images)
                : selectedImages,
        [smartOrder, selectedImages, images]
    );

    // Auto-regenerate slide text when the image set changes (added, removed,
    // swapped, or reordered): clear existing text so the text step re-generates
    // for the new sequence. The signature is the ordered list of image ids; the
    // initial value is skipped so we don't wipe before the first generation.
    const slidesSignature = useMemo(
        () => orderedSelectedImages.map((s) => `${s.type}:${s.id}`).join("|"),
        [orderedSelectedImages]
    );
    const prevSlidesSignatureRef = useRef<string>("");
    useEffect(() => {
        if (
            prevSlidesSignatureRef.current &&
            prevSlidesSignatureRef.current !== slidesSignature
        ) {
            setTexts([]);
            setAiTexts([]);
            setCaptionRenderId(undefined);
        }
        prevSlidesSignatureRef.current = slidesSignature;
    }, [slidesSignature]);

    // One slide per selected image (in tour order), resolved to a preview URL.
    const slides = useMemo(
        () =>
            orderedSelectedImages.map((sel) => {
                const imageMatch =
                    sel.type === "asset"
                        ? undefined
                        : images.find((i) => i.id === sel.id);
                const match =
                    sel.type === "asset"
                        ? assets.find((a) => a.id === sel.id)
                        : imageMatch;
                return {
                    id: sel.id,
                    previewUrl:
                        match?.signedUrl || match?.thumbnail || match?.url || "",
                    // Only listing images carry a tag/description; assets don't.
                    tag: imageMatch?.tag ?? "",
                    description: imageMatch?.description ?? "",
                };
            }),
        [orderedSelectedImages, images, assets]
    );

    useEffect(() => {
        // Gate on isAuthenticated (not just socket): the server only honours
        // subscribeToSlideshow after the connection is authenticated, and
        // isAuthenticated flips false→true on every (re)connect — so depending
        // on it re-runs this effect after a dropped/recycled socket, rejoining
        // the slideshow room. Without this the completion event is emitted to a
        // room the client silently left and the UI stays stuck "Generating".
        if (!socket || !isAuthenticated) return;
        if (!jobId) return;

        // Capture the current jobId to use in cleanup
        const currentJobId = jobId;

        socket.emit("subscribeToSlideshow", currentJobId);
        subscribedSlideshowRef.current.add(currentJobId);

        // MB-1396: the socket event and the reconcile fetch below can both report
        // the same outcome. Whichever arrives first settles the job; the other is
        // ignored, so the user never sees the toast twice.
        const settleJob = (status: JobStatus, reason?: string) => {
            if (settledJobsRef.current.has(currentJobId)) return;
            settledJobsRef.current.add(currentJobId);

            setJobId(null);
            setJobStatus(null);

            if (status === JobStatus.COMPLETED) {
                fetchListing();
                toast.success(
                    <CustomToast
                        message="Slideshow generation completed."
                        type="success"
                    />,
                    { autoClose: 3000 }
                );
                return;
            }

            toast.error(
                <CustomToast
                    message={
                        reason === SERVICE_UNAVAILABLE_REASON
                            ? VIDEO_SERVICE_UNAVAILABLE_MESSAGE
                            : "Slideshow generation failed. Please try again."
                    }
                    type="error"
                />,
                { autoClose: 3000 }
            );
        };

        const handleProgressUpdate = (data: {
            entityId: string;
            entityType: "slideshow";
            progress: JobStatus;
            reason?: string;
        }) => {
            const { entityId, entityType, progress, reason } = data;
            if (entityType === "slideshow" && currentJobId === entityId) {
                setJobStatus(progress);

                if (
                    progress === JobStatus.COMPLETED ||
                    progress === JobStatus.FAILED
                ) {
                    settleJob(progress, reason);
                }
            }
        };

        socket.on("progressUpdate", handleProgressUpdate);

        // MB-1396: a render can settle BEFORE this subscription lands — a dispatch
        // failure resolves in milliseconds when the video service is down, and the
        // API claims the failure as it emits, so no watchdog will re-announce it.
        // The event goes to an empty room and the modal spins forever. Reading the
        // job's own status once, after subscribing, closes that window: subscribe
        // first so nothing can slip between the two.
        let cancelled = false;
        (async () => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const authHeader = await getAuthHeader();
                const { data } = await axios.get<{
                    status: JobStatus | null;
                    failureReason: string | null;
                }>(`${backendUrl}/listing/slideshow-jobs/${currentJobId}`, {
                    headers: authHeader,
                });

                if (cancelled) return;
                if (
                    data.status === JobStatus.COMPLETED ||
                    data.status === JobStatus.FAILED
                ) {
                    settleJob(data.status, data.failureReason ?? undefined);
                }
            } catch {
                // Best effort — the socket remains the primary path, and a failed
                // reconcile must never itself break the render UI.
            }
        })();

        const localSubscribedSlideshow = subscribedSlideshowRef.current;

        return () => {
            cancelled = true;
            socket.off("progressUpdate", handleProgressUpdate);

            // Use the captured currentJobId in cleanup
            if (localSubscribedSlideshow.has(currentJobId)) {
                socket.emit("unsubscribeFromSlideshow", currentJobId);
                localSubscribedSlideshow.delete(currentJobId);
            }
        };
    }, [socket, isAuthenticated, jobId, setJobStatus, setJobId, fetchListing]);

    const handleMediaSelect = (id: string, type: "image" | "asset") => {
        // Crossing the motion-template ceiling is allowed — the template is not
        // chosen until step 2 — so this only informs, once. Checked outside the
        // state updater so the updater stays free of side effects.
        if (
            !selectedImages.some((image) => image.id === id) &&
            selectedImages.length === KEN_BURNS_MAX_IMAGES &&
            !kenBurnsNoticeShown.current
        ) {
            kenBurnsNoticeShown.current = true;
            setShowKenBurnsNotice(true);
        }
        setSelectedImages((prevSelectedImages) => {
            // If the image is already selected, remove it
            if (prevSelectedImages.some((image) => image.id === id)) {
                return prevSelectedImages.filter((image) => image.id !== id);
            } else {
                // Check if maximum limit is reached
                if (prevSelectedImages.length >= MAX_IMAGES) {
                    toast.error(
                        <CustomToast
                            message={`You can select up to ${MAX_IMAGES} images.`}
                            type="error"
                        />
                    );
                    return prevSelectedImages;
                }
                // Add the new image ID to the end of the array
                return [...prevSelectedImages, { id, type }];
            }
        });
    };

    const resetState = () => {
        setStep(1);
        setSelectedImages([]);
        kenBurnsNoticeShown.current = false;
        setShowKenBurnsNotice(false);
        setSmartOrder(true);
        setSelectedTemplate(DEFAULT_SLIDESHOW_TEMPLATE);
        setColorSource(undefined);
        setBrandColors(undefined);
        setSelectedFont("Inter");
        setTexts([]);
        setAiTexts([]);
        setCaptionRenderId(undefined);
        setIsGeneratingText(false);
        setSelectedLogo(null);
        setSavedLogoRef(null);
        setSelectedOverlay(null);
        setSelectedOverlayUrl(null);
        setIncludeMusic(false);
        setMusic([]);
        setSelectedMusic("");
        // Cleared synchronously: seedFromSettings is invoked in the same tick,
        // before the mirroring effect below has had a chance to run.
        selectedMusicRef.current = "";
        setBrandingValidateAttempted(false);
    };

    // Start fresh every time the dialog opens — closing via the X / Esc /
    // overlay doesn't unmount this component, so stale selections would
    // otherwise carry over to the next open.
    // Seeding is invoked here, after resetState(), so the order the two apply
    // in is explicit. Doing it from a separate effect would leave correctness
    // resting on seedFromSettings awaiting before its first write.
    useEffect(() => {
        if (!isOpen) {
            // Supersede any seed still in flight for the open we just closed.
            openSeqRef.current += 1;
            return;
        }
        resetState();
        void seedFromSettings(++openSeqRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleGenerate = async () => {
        // Guard against a required branding field cleared after step 2 (e.g. via
        // Back); send the user back to fix it rather than submitting an invalid job.
        if (hasBrandingErrors) {
            setBrandingValidateAttempted(true);
            setStep(2);
            toast.error(
                <CustomToast
                    message="Some required branding fields are missing."
                    type="error"
                />,
                { autoClose: 4000 }
            );
            return;
        }

        setIsGenerating(true);
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            // NOTE: the Video Micro-Service V2 request contract is owned by the
            // marabot-video team. `colors` is the same 3-value brand array as
            // ZapCap ([main, second, third]); `person` (Corporate Headshot),
            // `logo` and agent details are resolved server-side from the user's
            // branding profile.
            const response = await axios.post(
                `${backendUrl}/listing/slideshow/`,
                {
                    listingId,
                    listingType,
                    imageList: orderedSelectedImages,
                    template: selectedTemplate,
                    colors: brandColors
                        ? [
                              brandColors.main,
                              brandColors.secondary,
                              brandColors.accent,
                          ]
                        : undefined,
                    // Persist custom colors as the branding preference, like ZapCap.
                    useCustomColorsForZapcap: colorSource?.type === "custom",
                    font: selectedFont,
                    logo: selectedLogo,
                    // Classic uses a reusable overlay asset; V2 templates don't.
                    overlay: selectedOverlay ?? undefined,
                    texts: texts.filter(
                        (t) => t.header.trim() || t.subHeader.trim()
                    ),
                    // The original AI draft, kept verbatim for audit.
                    aiTexts,
                    // MB-1314 (O3): links the debug artifact recorded at caption time to
                    // the render that actually used it. The captions may have been edited
                    // since — the artifact holds what the model produced, the job holds
                    // what shipped, and comparing them is the whole point.
                    captionRenderId,
                    audioLink: includeMusic ? selectedMusic : null,
                },
                { headers }
            );

            if (response.status === 201) {
                setJobId(response.data);
                setJobStatus(JobStatus.QUEUED);
                setIsGenerating(false);
                resetState();
                onClose();
                toast.success(
                    <CustomToast
                        message="Slideshow generation started. You will be notified once it's ready."
                        type="success"
                    />,
                    { autoClose: 3000 }
                );
            } else {
                throw new Error("Error generating slideshow");
            }
        } catch (error) {
            setIsGenerating(false);
            toast.error(
                <CustomToast
                    message="An error occurred while generating the slideshow. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
            onClose();
        }
    };

    // V2 templates show a frozen-frame preview that mirrors the rendered video;
    // Classic (video1) shows the photos with the optional address overlay.
    const previewNode = isVideo1 ? (
        <ClassicPreview
            photoUrls={slides.map((s) => s.previewUrl)}
            overlayUrl={selectedOverlayUrl}
        />
    ) : (
        <SlideshowPreview
            template={selectedTemplate}
            brandColors={brandColors}
            font={selectedFont}
            logo={selectedLogo}
            person={corporateHeadshot}
            firstname={agentFirstName ?? ""}
            lastname={agentLastName ?? ""}
            contactNumber={agentPhone ?? ""}
            texts={texts}
            photoUrls={slides.map((s) => s.previewUrl)}
            recentlyListed={recentlyListed}
        />
    );

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="relative flex h-[calc(90dvh-2rem)] flex-col overflow-y-hidden p-0 bg-white min-[1200px]:max-w-6xl sxl:max-w-7xl">

                <div className="relative shrink-0 border-b border-neutral-200 bg-white px-4 py-3 min-[640px]:px-6 min-[768px]:px-8">
                    <button
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary hover:text-primary focus:outline-none"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FaTimes className="h-6 w-6" />
                    </button>
                    <DialogHeader className="px-8">
                        <DialogTitle className="text-lg min-[640px]:text-xl min-[768px]:text-2xl">
                            Create Slideshow
                        </DialogTitle>
                        <DialogDescription className="text-sm min-[640px]:text-base min-[768px]:text-lg">
                            {step === 1 &&
                                "Select the images for your slideshow"}
                            {step === 2 &&
                                "Choose a template, then set your brand colors, logo, font, headshot and music"}
                            {step === 3 &&
                                "Add the headline and sub-text shown over your slides"}
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <div
                    ref={bodyRef}
                    className={`flex min-h-0 flex-1 flex-col ${
                        isCreatingOverlay
                            ? "overflow-hidden"
                            : "space-y-6 overflow-y-auto px-4 pb-4 pt-4 min-[640px]:px-6 min-[640px]:pb-6 min-[768px]:px-8 min-[768px]:pb-8"
                    }`}
                >
                {step === 1 && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between gap-4 rounded-xl border border-neutral-200 bg-white p-4">
                            <div>
                                <h4 className="text-sm font-semibold text-primary">
                                    Smart Photo Order
                                </h4>
                                <p className="text-xs text-neutral-500">
                                    Arrange your photos into a natural home tour
                                    — exterior, living areas, kitchen, bedrooms,
                                    then outdoor. Turn off to keep your own
                                    order.
                                </p>
                            </div>
                            <Switch
                                checked={smartOrder}
                                onCheckedChange={setSmartOrder}
                                checkedLabel="On"
                                uncheckedLabel="Off"
                                aria-label="Smart photo order"
                            />
                        </div>
                        <p className="text-xs text-neutral-500">
                            Motion templates (Spotlight Motion) support up to{" "}
                            {KEN_BURNS_MAX_IMAGES} photos. Every other template
                            takes up to {MAX_IMAGES}.
                        </p>
                        <SlideshowMediaSelector
                            media={{ images, assets }}
                            selectedImages={selectedImages}
                            handleMediaSelect={handleMediaSelect}
                        />
                    </div>
                )}

                {step === 2 && (
                    <div
                        className={
                            isCreatingOverlay
                                ? "flex min-h-0 flex-1 flex-col"
                                : "grid grid-cols-1 gap-6 min-[1200px]:grid-cols-[minmax(0,1fr)_30rem]"
                        }
                    >
                        <SlideshowBranding
                            selectedTemplate={selectedTemplate}
                            selectedImageCount={selectedImages.length}
                            setSelectedTemplate={setSelectedTemplate}
                            onBlockedTemplateSelect={() =>
                                setShowKenBurnsNotice(true)
                            }
                            colorSource={colorSource}
                            setColorSource={setColorSource}
                            setBrandColors={setBrandColors}
                            selectedFont={selectedFont}
                            setSelectedFont={setSelectedFont}
                            selectedLogo={selectedLogo}
                            setSelectedLogo={setSelectedLogo}
                            selectedLogoReference={savedLogoRef}
                            listingId={listingId}
                            listingType={listingType}
                            selectedOverlay={selectedOverlay}
                            setSelectedOverlay={setSelectedOverlay}
                            onSelectedOverlayUrlChange={setSelectedOverlayUrl}
                            formattedAddress={formattedAddress}
                            sampleImage={slides[0]?.previewUrl}
                            creatingOverlay={isCreatingOverlay}
                            setCreatingOverlay={setIsCreatingOverlay}
                            includeMusic={includeMusic}
                            setIncludeMusic={setIncludeMusic}
                            music={music}
                            setMusic={setMusic}
                            selectedMusic={selectedMusic}
                            setSelectedMusic={setSelectedMusic}
                            errors={
                                brandingValidateAttempted
                                    ? brandingErrors
                                    : undefined
                            }
                        />
                        {!isCreatingOverlay && (
                            <div>
                                <div className="min-[1200px]:sticky min-[1200px]:top-4">
                                    {previewNode}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {step === 3 && (
                    <SlideshowTextEditor
                        slides={slides}
                        texts={texts}
                        setTexts={setTexts}
                        setAiTexts={setAiTexts}
                        setCaptionRenderId={setCaptionRenderId}
                        listingId={listingId}
                        listingType={listingType}
                        template={selectedTemplate}
                        isGeneratingText={isGeneratingText}
                        setIsGeneratingText={setIsGeneratingText}
                    />
                )}
                </div>

                {!isCreatingOverlay && (
                <div className="shrink-0 border-t border-neutral-200 bg-white px-4 py-4 min-[640px]:px-6 min-[768px]:px-8">
                {/* Navigation Buttons */}
                <div
                    className={`flex ${
                        step === 1
                            ? "justify-end"
                            : "flex-col min-[640px]:flex-row justify-between space-y-4 min-[640px]:space-y-0"
                    }`}
                >
                    {step === 1 && (
                        <Button
                            onClick={continueToBranding}
                            disabled={selectedImages.length < MIN_IMAGES}
                            className="w-full min-[640px]:w-auto"
                        >
                            Continue to Template & Branding
                        </Button>
                    )}

                    {step === 2 && (
                        <>
                            <Button
                                variant="secondary"
                                onClick={() => setStep(1)}
                                className="w-full min-[640px]:w-auto"
                            >
                                Back to Image Selection
                            </Button>
                            <Button
                                onClick={
                                    isVideo1
                                        ? handleGenerate
                                        : continueFromBranding
                                }
                                disabled={
                                    (isVideo1 && isGenerating) ||
                                    exceedsKenBurnsLimit
                                }
                                className="w-full min-[640px]:w-auto"
                            >
                                {isVideo1 ? (
                                    isGenerating ? (
                                        <div className="flex items-center justify-center">
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Generating…
                                        </div>
                                    ) : (
                                        "Generate Slideshow"
                                    )
                                ) : (
                                    "Continue to Slide Text"
                                )}
                            </Button>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <Button
                                variant="secondary"
                                onClick={() => setStep(2)}
                                className="w-full min-[640px]:w-auto"
                            >
                                Back to Template & Branding
                            </Button>
                            <Button
                                onClick={handleGenerate}
                                disabled={
                                    isGenerating ||
                                    isGeneratingText ||
                                    selectedImages.length === 0 ||
                                    !selectedTemplate ||
                                    exceedsKenBurnsLimit
                                }
                                className="w-full min-[640px]:w-auto"
                            >
                                {isGenerating ? (
                                    <div className="flex items-center justify-center">
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Generating...
                                    </div>
                                ) : (
                                    "Generate Slideshow"
                                )}
                            </Button>
                        </>
                    )}
                </div>
                </div>
                )}

                <ConfirmPopup
                    open={showKenBurnsNotice}
                    title="Heads up"
                    message={`Motion templates support up to ${KEN_BURNS_MAX_IMAGES} photos.`}
                    messageTwo="You can keep adding photos - every other template takes up to 16."
                    confirmText="Got it"
                    hideCancelButton
                    onConfirm={() => setShowKenBurnsNotice(false)}
                    onCancel={() => setShowKenBurnsNotice(false)}
                />
            </DialogContent>
        </Dialog>
    );
};

export default Slideshow;
